import { useEffect, useState } from 'react';

/**
 * Publishes `--rsv-nav-offset` on the page element: the distance from the
 * navbar's top to the bottom of its (optional, conditionally-mounted)
 * `<Subnavbar>`. The full-height workspace shells (`pageContent={false}`) let
 * their inner scrollers and floating controls read it as top padding, so
 * content slides UNDER the blurred bar instead of starting at y=0 and being
 * clipped.
 *
 * Why we measure instead of using F7's `--f7-page-navbar-offset`: with
 * `pageContent={false}` and a `<Subnavbar>` that mounts/unmounts per view,
 * F7's navbar layout flags are set once at init and aren't reactive to React
 * adding/removing the subnavbar - the built-in offset goes stale on every
 * view switch (dead band above the canvas, or content sliding under the bar).
 * We measure real geometry via getBoundingClientRect, which doesn't care how
 * the layout is composed (on iOS F7 positions `.subnavbar` absolutely, so it
 * doesn't contribute to `navbar.offsetHeight` either).
 *
 * The MutationObserver is the key to surviving view switches: it re-measures
 * and rebinds the size observer whenever the subnavbar appears/disappears, so
 * no dependency key or per-page mode wiring is needed - the hook self-heals.
 *
 * Returns the measured offset in px, so callers that need the value in JS
 * (e.g. to inset a full-height canvas's fit below the bar) don't have to
 * re-read the CSS variable themselves.
 *
 * Shared by `ReservationsWorkspacePage` and `TablesWorkspacePage`.
 */
export function useNavOffset(pageName: string): number {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const page = document.querySelector(`.page[data-name="${pageName}"]`) as HTMLElement | null;
    const navbar = page?.querySelector('.navbar') as HTMLElement | null;
    if (!page || !navbar) return;
    const writeOffset = () => {
      const subnavbar = navbar.querySelector('.subnavbar') as HTMLElement | null;
      const navTop = navbar.getBoundingClientRect().top;
      const bottom = (subnavbar ?? navbar).getBoundingClientRect().bottom;
      const value = Math.max(0, bottom - navTop);
      page.style.setProperty('--rsv-nav-offset', `${value}px`);
      setOffset(value);
    };
    writeOffset();
    const ro = new ResizeObserver(writeOffset);
    ro.observe(navbar);
    // The subnavbar may not exist on first run, or may mount/unmount between
    // view switches. Watch the navbar subtree and rebind the size observer to
    // whatever subnavbar is currently mounted.
    const mo = new MutationObserver(() => {
      writeOffset();
      const sub = navbar.querySelector('.subnavbar') as HTMLElement | null;
      if (sub) ro.observe(sub);
    });
    mo.observe(navbar, { childList: true, subtree: true });
    const subnavbar = navbar.querySelector('.subnavbar') as HTMLElement | null;
    if (subnavbar) ro.observe(subnavbar);
    // Re-measure next frame too - F7's imperative subnavbar setup sometimes
    // lands a tick after the React mount.
    const raf = requestAnimationFrame(writeOffset);
    // On the iOS theme F7 sizes/positions the navbar + (absolute) subnavbar
    // imperatively, a few ticks AFTER mount - later than a single rAF. Until it
    // settles the offset reads short and content hides behind the bar (most
    // visible on the List view, whose title + first cards sit at the very top).
    // Re-measure at a few delays to catch that deferred layout, and on
    // resize / orientation / theme-driven relayout.
    const timers = [50, 150, 300, 600].map((ms) => window.setTimeout(writeOffset, ms));
    window.addEventListener('resize', writeOffset);
    window.addEventListener('orientationchange', writeOffset);
    return () => {
      ro.disconnect();
      mo.disconnect();
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', writeOffset);
      window.removeEventListener('orientationchange', writeOffset);
    };
  }, [pageName]);
  return offset;
}
